import { CERTIFICATION_COURSES } from '@common/constants/certification.constants'
import { CertificationFormValues, TraineeCertification } from '@common/types/certification.types'
import Dialog from '@components/layout/Dialog'
import { useCertificationApi } from '@services/api/certification.api'
import { useCertification } from '@services/contexts/CertificationContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import CertificationPopIn from './CertificationPopIn'

type CertificationUpdatePopInProps = {
	certification: TraineeCertification
	isOpen: boolean
	onClose: () => void
	onSuccess: () => void
	traineeId: string
}

/**
 * Certification creation pop-in
 * @param isOpen
 * @param onClose
 * @param onSuccess
 * @param traineeId
 * @param certification
 * @constructor
 */
const CertificationUpdatePopIn = (
	{
		isOpen,
		onClose,
		onSuccess,
		traineeId,
		certification
	}: CertificationUpdatePopInProps
) => {
	const { setCertification } = useCertification()
	const { deleteCertification, updateCertification } = useCertificationApi()
	const { formatMessage } = useIntl()
	const { addErrorSnackbar, addSnackbar } = useSnackbar()
	const [isDeletionPopInOpen, setIsDeletionPopInOpen] = useState(false)

	const form = useForm<CertificationFormValues>({
		defaultValues: {
			course: certification.course || CERTIFICATION_COURSES[0],
			obtaining: {
				isTrainer: certification.isTrainer
			},
			date: certification.completionDate || new Date().toISOString(),
			trainerId: certification.trainerId
		}
	})

	const { mutate: updateCertificationMutate } = useMutation<TraineeCertification, Error, CertificationFormValues>({
		mutationKey: ['updateCertification', certification.id],
		mutationFn: (values) => updateCertification(certification.id, values),
		onSuccess: (data) => {
			onSuccess()
			setCertification({
				course: data.course,
				date: data.trainedOn,
				obtaining: {
					isTrainer: data.isTrainer
				},
				trainerId: data.trainerId
			})
			addSnackbar(formatMessage({ id: 'trainee.certifications.actions.update.success' }))
		},
		onError: () => addErrorSnackbar()
	})

	const { mutate: deleteCertificationMutate } = useMutation({
		mutationKey: ['deleteCertification'],
		mutationFn: deleteCertification,
		onSuccess: () => {
			addSnackbar(formatMessage({ id: 'trainee.certifications.actions.delete.success' }))
			onSuccess()
		},
		onError: () => addErrorSnackbar()
	})

	const onSubmit = (values: CertificationFormValues) => updateCertificationMutate(values)

	return (
		<FormProvider {...form}>
			<CertificationPopIn
				title={formatMessage({ id: 'trainee.certifications.actions.update.title' })}
				subtitle={formatMessage({ id: 'trainee.certifications.actions.update.subtitle' })}
				buttons={[
					{
						label: formatMessage({ id: 'trainee.certifications.actions.update.buttons.confirm.label' }),
						onClick: form.handleSubmit(onSubmit)
					},
					{
						label: formatMessage({ id: 'global.buttons.delete' }),
						onClick: () => setIsDeletionPopInOpen(true),
						variant: 'tertiary'
					}
				]}
				traineeId={traineeId}
				onClose={onClose}
				isOpen={isOpen}
			/>
			{isDeletionPopInOpen && (
				<Dialog
					title={formatMessage({ id: 'trainee.certifications.actions.delete.title' })}
					message={formatMessage({ id: 'trainee.certifications.actions.delete.message' })}
					confirmLabel={formatMessage({ id: 'global.buttons.delete' })}
					onClose={() => setIsDeletionPopInOpen(false)}
					onCancel={() => setIsDeletionPopInOpen(false)}
					onConfirm={() => deleteCertificationMutate(certification.id)}
					isOpen
				/>
			)}
		</FormProvider>
	)
}

export default CertificationUpdatePopIn
